import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCdnImage } from '../lib/imageUtils';

export default function Footer() {
  const location = useLocation();
  const isServicesOverview = location.pathname === '/ydelser' || location.pathname === '/hvad-laver-vi';

  return (
    <footer className="bg-slate-950 text-slate-300 border-t border-slate-800">
      {/* Pre-footer Call To Action Strip */}
      {!isServicesOverview && (
        <div className="bg-gradient-to-r from-emerald-900 via-emerald-800 to-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8 border-b border-emerald-950">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="text-center lg:text-left">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold uppercase tracking-wider mb-2 border border-emerald-500/30">
                <i className="fa-solid fa-hard-hat text-xs" aria-hidden="true" />
                Klar til dit næste projekt
              </span>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                Brug for en erfaren entreprenør eller kloakmester?
              </h3>
              <p className="text-emerald-100/80 text-sm sm:text-base mt-1 max-w-2xl">
                Vi yder professionel assistance til vandværker, kloakarbejde, anlæg og vinterdrift på Lolland-Falster.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 flex-shrink-0">
              <a
                href="tel:40309708"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl bg-white text-slate-950 hover:bg-slate-100 font-bold text-sm shadow-lg transition-all whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-emerald-700" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring 40 30 97 08</span>
              </a>
              <Link
                to="/kontakt-os"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-lg transition-all border border-emerald-400/40 whitespace-nowrap"
              >
                <span>Få et tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Col 1: Brand & Bio */}
          <div className="space-y-4">
            <Link to="/" className="inline-block bg-white p-2 rounded-lg" aria-label="Forside">
              <img
                src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/entreprenrlang.jpg', {
                  width: 260,
                  quality: 90,
                })}
                alt="ENTREPRENØR LANG ApS"
                className="h-12 w-auto object-contain"
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed">
              ENTREPRENØR LANG ApS blev stiftet i 2005 af Martin Hovmand Lang. Vi leverer solidt og autoriseret entreprenørarbejde med fokus på kvalitet og ordentligt håndværk.
            </p>

            {/* Badges */}
            <div className="pt-2 flex flex-wrap items-center gap-3">
              <div className="bg-white p-2 rounded-lg shadow-sm border border-slate-700 inline-flex items-center justify-center">
                <img
                  src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/dme-kloakmestergaranti.webp', {
                    width: 440,
                    quality: 90,
                  })}
                  alt="DM&E Kloakmestergaranti"
                  className="h-20 w-auto object-contain"
                />
              </div>
            </div>
          </div>

          {/* Col 2: Services */}
          <div>
            <h4 className="text-white text-base font-bold tracking-tight mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              Vores ydelser
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link
                  to="/ydelser/vandvaerksservice-ledningsarbejde"
                  className="text-slate-400 hover:text-emerald-400 transition-colors flex items-center gap-2"
                >
                  <i className="fa-solid fa-angle-right text-xs text-emerald-500" aria-hidden="true" />
                  <span>Vandværksservice & ledninger</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/ydelser/kloakarbejde-tilslutning"
                  className="text-slate-400 hover:text-emerald-400 transition-colors flex items-center gap-2"
                >
                  <i className="fa-solid fa-angle-right text-xs text-emerald-500" aria-hidden="true" />
                  <span>Autoriseret kloakarbejde</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/ydelser/belaegning-anlaeg"
                  className="text-slate-400 hover:text-emerald-400 transition-colors flex items-center gap-2"
                >
                  <i className="fa-solid fa-angle-right text-xs text-emerald-500" aria-hidden="true" />
                  <span>Belægning & anlæg</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/ydelser/vinterservice-snerydning"
                  className="text-slate-400 hover:text-emerald-400 transition-colors flex items-center gap-2"
                >
                  <i className="fa-solid fa-angle-right text-xs text-emerald-500" aria-hidden="true" />
                  <span>Vinterservice & snerydning</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/ydelser"
                  className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors flex items-center gap-2 pt-1"
                >
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  <span>Se alle fagområder</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Navigation */}
          <div>
            <h4 className="text-white text-base font-bold tracking-tight mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              Hurtige links
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/" className="text-slate-400 hover:text-emerald-400 transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-angle-right text-xs text-emerald-500" aria-hidden="true" />
                  <span>Forside</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/galleri"
                  className="text-slate-400 hover:text-emerald-400 transition-colors flex items-center gap-2"
                >
                  <i className="fa-solid fa-angle-right text-xs text-emerald-500" aria-hidden="true" />
                  <span>Billedgalleri</span>
                </Link>
              </li>
              <li>
                <Link to="/om-os" className="text-slate-400 hover:text-emerald-400 transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-angle-right text-xs text-emerald-500" aria-hidden="true" />
                  <span>Om ENTREPRENØR LANG</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/kontakt-os"
                  className="text-slate-400 hover:text-emerald-400 transition-colors flex items-center gap-2"
                >
                  <i className="fa-solid fa-angle-right text-xs text-emerald-500" aria-hidden="true" />
                  <span>Kontakt os</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Contact details */}
          <div>
            <h4 className="text-white text-base font-bold tracking-tight mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              Virksomhedsoplysninger
            </h4>
            <div className="space-y-3 text-sm text-slate-400">
              <p className="font-bold text-white">ENTREPRENØR LANG ApS</p>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot text-emerald-400 mt-1" aria-hidden="true" />
                <span>
                  Karlebyvej 32<br />
                  4894 Øster Ulslev<br />
                  Danmark
                </span>
              </div>
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-emerald-400" aria-hidden="true" />
                <a
                  href="tel:40309708"
                  className="text-slate-200 hover:text-emerald-400 font-semibold transition-colors whitespace-nowrap"
                >
                  40 30 97 08
                </a>
              </div>
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-emerald-400" aria-hidden="true" />
                <a
                  href="mailto:martinl4894@gmail.com"
                  className="text-slate-200 hover:text-emerald-400 transition-colors whitespace-nowrap"
                >
                  martinl4894@gmail.com
                </a>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500 pt-1">
                <i className="fa-solid fa-id-card text-emerald-400" aria-hidden="true" />
                <span>CVR-nr: 28498675</span>
              </div>

              {/* Social / Facebook */}
              <div className="pt-3">
                <a
                  href="https://www.facebook.com/p/Entrepren%C3%B8r-Lang-100054529146504/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 hover:bg-emerald-900/50 border border-slate-800 text-slate-300 hover:text-white text-xs transition-colors"
                >
                  <i className="fa-brands fa-facebook text-emerald-400 text-sm" aria-hidden="true" />
                  <span>Følg os på Facebook</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom copyright */}
        <div className="mt-14 pt-8 border-t border-slate-900 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-4">
          <p>© {new Date().getFullYear()} ENTREPRENØR LANG ApS · CVR: 28498675. Alle rettigheder forbeholdes.</p>
          <div className="flex items-center gap-4">
            <span className="text-slate-600">Aut. kloakmester & entreprenør</span>
            <span className="text-slate-700">·</span>
            <span className="text-slate-600">Øster Ulslev</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
